import { Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  merge,
  Observable,
  Subject,
} from "rxjs";
import {
  filter,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { AlertsService } from "src/app/core/alerts.service";
import { ApiError } from "src/app/core/api-error";
import { Permission, UserService } from "src/app/core/auth";
import { PaginationV2 } from "src/app/core/pagination-v2";
import { PrefixedRequestService } from "src/app/core/prefixed-request.service";
import { RequestService } from "src/app/core/request.service";
import { Site, SitesService } from "src/app/core/sites";
import { ColumnFilter } from "src/app/core/table";
import { TicketCountsService } from "src/app/core/ticket-counts/ticket-counts.service";
import { Partner } from "src/app/partner/global/partner.model";
import { HelpAssistListQueryModel } from "src/app/partner/help-assist/help-assist-list-query.model";
import { HelpAssistAppointmentRequest } from "src/app/partner/help-assist/models/help-assist-appointment-request.model";
import { DashboardHelpAssistTicket } from "src/app/partner/help-assist/models/ticket/dashboard-help-assist-ticket.model";
import {
  HelpAssistTicket,
  helpTicketsResourceGlobal,
  helpTicketsResourcePartner,
  PartnerHelpAssistTicket,
} from "src/app/partner/help-assist/models/ticket/help-assist-ticket.model";
import {
  ColumnSort,
  firstExistentValueFrom,
  isExistent,
  isInstanceOf,
  isOneOfCreator,
  mustHaveEvery,
  parseNumber,
  pluckDistinctUntilChanged,
  SortOrder,
  switchMapNullable,
  UUID,
} from "src/utils";
import { normalizeColumnSort } from "src/utils/set-sort-on-resource";
import { Attachment } from "./attachment.model";
import { AttachmentsService } from "./attachments.service";
import {
  HelpAssistTicketStatus,
  HelpAssistTicketType,
} from "./help-assist-enums";
import { HelpAssistEventCreation } from "./help-assist-event-creation.model";
import { HelpAssistEvent } from "./help-assist-event.model";
import { HelpAssistEventsService } from "./help-assist-events.service";
import { HelpAssistHasScheduledTicketForRequest } from "./models/help-assist-has-schedule-ticket-for-request.model";
import { HelpAssistMarkAllAsReadRequest } from "./models/help-assist-mark-all-as-read-request.model";
import { HelpAssistTicketApproveOrDeclineRequest } from "./models/help-assist-ticket-appointment-approve-or-decline-request.model";
import { HelpAssistTicketAssignUserRequest } from "./models/help-assist-ticket-assign-user-request.model";
import { HelpAssistTicketCloseAssociatedTicketsRequest } from "./models/help-assist-ticket-close-associated-tickets-request.model";
import { HelpAssistTicketDetailsUpdateRequest } from "./models/help-assist-ticket-details-update-request.model";
import { HelpAssistTicketStatusChangeRequest } from "./models/help-assist-ticket-status-change-request.model";
import { HelpAssistTicketReference } from "./models/ticket/base-help-assist-ticket.model";
import { HelpAssistTicketCreate } from "./models/ticket/help-assist-ticket-create.model";
import { AssociatedAppointment } from "./models/ticket-associated-appointment.model";
import { AssociatedTicket } from "./models/ticket-associated-ticket.model";

const ticketFilters = [
  "your-tickets",
  "open-tickets",
  "unassigned-tickets",
  "closed-tickets",
  "auto-approved-reservations",
] as const;
export type TicketFilter = (typeof ticketFilters)[number];
export const isTicketFilter = isOneOfCreator(ticketFilters);

const ticketCountFilters = ["pending-approval-tickets"] as const;
export type TicketCountFilter = (typeof ticketCountFilters)[number];
export const isTicketCountFilter = isOneOfCreator(ticketCountFilters);

@Injectable({ providedIn: "root" })
export class HelpAssistService {
  public constructor(
    private readonly alerts: AlertsService,
    private readonly attachments: AttachmentsService,
    private readonly helpTicketEvents: HelpAssistEventsService,
    private readonly request: RequestService,
    private readonly prefixedRequest: PrefixedRequestService,
    private readonly route: ActivatedRoute,
    private readonly sites: SitesService,
    private readonly ticketCounts: TicketCountsService,
    private readonly user: UserService,
  ) {}

  public readonly modifiableResourceChanges = this.sites
    .getResourceInSelectedPartner(helpTicketsResourcePartner)
    .pipe(shareReplay(1));

  private readonly globalHelpAssistListDeserializeArguments = combineLatest([
    this.ticketCounts.changes.pipe(pluckDistinctUntilChanged("partners")),
    this.sites.selectedChanges.pipe(pluckDistinctUntilChanged("timeZone")),
  ]).pipe(
    map(([partners, timeZone]) => {
      const sites = partners.flatMap(({ sites }) => sites);
      return mustHaveEvery({
        partners,
        sites,
        timeZone,
      });
    }),
    filter(isExistent),
  );

  private readonly markAllAsReadNotifierSubject = new BehaviorSubject<void>(
    undefined,
  );
  public readonly markAllAsReadNotifier =
    this.markAllAsReadNotifierSubject.asObservable();

  private readonly markAllAsReadLoadingSubject = new BehaviorSubject(false);
  public readonly markAllAsReadLoadingChanges =
    this.markAllAsReadLoadingSubject.asObservable();

  private readonly ticketUpdateSubject = new Subject<PartnerHelpAssistTicket>();
  public readonly ticketUpdate = this.ticketUpdateSubject.asObservable();

  private readonly receivedTicketSubject =
    new BehaviorSubject<PartnerHelpAssistTicket | null>(null);

  public readonly selectedTicketChanges = merge(
    this.receivedTicketSubject,
    this.ticketUpdate,
  );

  private readonly selectedTicketErrorSubject = new BehaviorSubject<unknown>(
    null,
  );
  public readonly selectedTicketErrorChanges =
    this.selectedTicketErrorSubject.asObservable();

  public readonly queriedHelpAssistTicketChanges = combineLatest([
    this.route.queryParamMap,
    this.ticketUpdate.pipe(startWith(null)),
  ]).pipe(
    map(([params]) => parseNumber(params.get("ticket"))),
    switchMapNullable((ticketId) =>
      this.receivedTicketSubject.value?.id === ticketId
        ? this.selectedTicketChanges
        : this.getSelected(ticketId),
    ),
    shareReplay(1),
  );

  public ticketModifyDisabledChanges = combineLatest([
    this.selectedTicketChanges,
    this.sites.selectedChanges,
  ]).pipe(
    map(([ticket, site]) =>
      isModifyTicketDisabled({
        ticket,
        site,
        userPermissions: this.user.permissions,
      }),
    ),
  );

  public getListChanges({
    tabFilter,
    columnFilters = [],
    sort = { key: "lastEventCreatedOn", order: SortOrder.Descending },
    pagination = new PaginationV2(),
  }: {
    tabFilter?: TicketFilter;
    columnFilters?: readonly ColumnFilter[];
    sort?: ColumnSort | StringKeys<DashboardHelpAssistTicket>;
    pagination?: PaginationV2;
  } = {}): Observable<readonly DashboardHelpAssistTicket[] | null> {
    const normalizedColumnSort = normalizeColumnSort(sort);

    return this.prefixedRequest.getPaged({
      prefix: "site",
      path: "/HelpAssistTickets/All",
      data: (site) =>
        new HelpAssistListQueryModel({
          pagination,
          tabFilter,
          columnFilters,
          sort: normalizedColumnSort,
          timezone: site.timeZone,
        }),
      pagination,
      mapResponse: (response, site) =>
        DashboardHelpAssistTicket.deserializeList(response.items, {
          site,
        }),
    });
  }

  public getGlobalListChanges({
    partnerKey,
    siteId,
    tabFilter,
    columnFilters = [],
    sort = { key: "lastEventCreatedOn", order: SortOrder.Descending },
    pagination = new PaginationV2(),
  }: {
    partnerKey?: Partner["key"] | null;
    siteId?: Site["id"] | null;
    tabFilter?: TicketFilter;
    columnFilters?: readonly ColumnFilter[];
    sort?: ColumnSort | StringKeys<DashboardHelpAssistTicket>;
    pagination?: PaginationV2;
  } = {}): Observable<readonly DashboardHelpAssistTicket[] | null> {
    const normalizedColumnSort = normalizeColumnSort(sort);

    return this.sites.selectedChanges.pipe(
      switchMap((site) =>
        this.prefixedRequest
          .getPaged({
            prefix: "global",
            path: "/helpAssistTickets",
            data: new HelpAssistListQueryModel({
              partnerKey,
              siteId,
              pagination,
              tabFilter,
              columnFilters,
              sort: normalizedColumnSort,
              timezone: site.timeZone,
            }),
            pagination,
          })
          .pipe(
            withLatestFrom(this.globalHelpAssistListDeserializeArguments),
            map(
              ([response, args]) =>
                response &&
                DashboardHelpAssistTicket.deserializeGlobalList(
                  response.items,
                  args,
                ),
            ),
          ),
      ),
    );
  }

  public getCountChanges({
    filter: ticketCountFilter,
    partnerKey,
    siteId,
  }: {
    filter?: TicketCountFilter;
    partnerKey?: UUID | null;
    siteId?: Site["id"] | null;
  } = {}): Observable<number | null> {
    let resource = helpTicketsResourceGlobal.withCount();

    if (partnerKey) {
      resource = resource.addFilter("partnerKey", "==", partnerKey);
    }

    if (siteId) {
      resource = resource.addFilter("siteId", "==", siteId);
    }

    switch (ticketCountFilter) {
      case "pending-approval-tickets":
        resource = resource
          .addFilter("status", "!=", HelpAssistTicketStatus.Closed)
          .addFilter("status", "!=", HelpAssistTicketStatus.Expired)
          .addFilter(
            ["appointmentSchedule", "appointmentApprovalRequired"],
            "==",
            true,
          );
        break;
    }

    resource = resource.top(0);

    return this.request
      .getAll(resource)
      .pipe(
        map((result) =>
          result && isExistent(result["@odata.count"])
            ? result["@odata.count"]
            : null,
        ),
      );
  }

  public async getAssociatedTickets(
    ticket: HelpAssistTicket,
  ): Promise<readonly AssociatedTicket[]> {
    const response = await firstExistentValueFrom(
      this.prefixedRequest.get({
        prefix: "site",
        path: "/HelpAssistTickets/AssociatedTickets/{key}",
        pathParams: {
          key: ticket.id,
        },
      }),
    );

    return AssociatedTicket.deserializeList(response, {
      associatedTicket: ticket,
    });
  }

  public async getAssociatedAppointments(
    ticket: HelpAssistTicket,
  ): Promise<readonly AssociatedAppointment[]> {
    const response = await firstExistentValueFrom(
      this.prefixedRequest.get({
        prefix: "site",
        path: "/HelpAssistTickets/AssociatedAppointments/{key}",
        pathParams: {
          key: ticket.id,
        },
      }),
    );

    return AssociatedAppointment.deserializeList(response, {
      associatedTicket: ticket,
    });
  }

  public async getSelected(
    id: HelpAssistTicket["id"],
    resetTicket = true,
  ): Promise<PartnerHelpAssistTicket | null> {
    try {
      if (resetTicket) {
        this.receivedTicketSubject.next(null);
      }
      this.selectedTicketErrorSubject.next(null);
      const ticket = await this.get(id);
      this.receivedTicketSubject.next(ticket);
      return ticket;
    } catch (error) {
      if (isInstanceOf(ApiError)(error)) {
        switch (error.status) {
          case 403: {
            this.alerts.warn({
              title: "Access Denied",
              message: "You do not have permission to view that page.",
            });
            this.selectedTicketErrorSubject.next(error);
            return null;
          }
          case 404: {
            this.alerts.warn({
              title: "Ticket Not Found",
              message: `The ticket with ID "${id}" does not exist.`,
            });
            this.selectedTicketErrorSubject.next(error);
            return null;
          }
          default:
            throw error;
        }
      }
      throw error;
    }
  }

  public async changeTicketStatus(
    request: HelpAssistTicketStatusChangeRequest,
  ): Promise<void> {
    await firstExistentValueFrom(
      this.prefixedRequest.post({
        prefix: "site",
        path: "/HelpAssistTickets/{id}/changeHelpAssistTicketStatusById",
        pathParams: { id: request.ticket.id },
        data: request,
      }),
    );
  }

  public async closeAssociatedTickets(
    request: HelpAssistTicketCloseAssociatedTicketsRequest,
    ticket: HelpAssistTicket,
  ): Promise<void> {
    await firstExistentValueFrom(
      this.prefixedRequest.post({
        prefix: "site",
        path: "/HelpAssistTickets/{id}/CloseAssociatedTickets",
        pathParams: { id: ticket.id },
        data: request,
      }),
    );

    // TODO: Remove this call once the API return the updated ticket
    await this.getSelected(ticket.id);
  }

  public async assignUser(
    request: HelpAssistTicketAssignUserRequest,
  ): Promise<void> {
    const resource = await firstValueFrom(this.modifiableResourceChanges);

    await firstValueFrom(
      this.request.post(
        resource
          .appendId(request.ticket.id)
          .appendFunctionPropertyPath("assignUser"),
        request,
      ),
    );

    this.ticketUpdateSubject.next(
      new PartnerHelpAssistTicket({
        ...request.ticket,
        assignedUser: request.user,
      }),
    );

    // TODO: Remove this call once the API return the full ticket object
    // from the assignUser endpoint. Currently we need to keep it to
    // update the ticket's events.
    await this.getSelected(request.ticket.id);
  }

  public async markAllAsRead(context?: {
    partnerKey: UUID;
    siteId: Site["id"] | null;
  }): Promise<void> {
    const request = new HelpAssistMarkAllAsReadRequest({
      partnerKey: context?.partnerKey ?? null,
      siteId: context?.siteId ?? null,
    });

    try {
      this.markAllAsReadLoadingSubject.next(true);

      await firstValueFrom(
        this.prefixedRequest
          .post({
            prefix: "global",
            path: "/HelpAssistTickets/MarkAllAsRead",
            data: request,
          })
          // Ignore the initial null emission from the POST request,
          // which is used to indicate the loading state
          .pipe(skip(1)),
      );

      this.markAllAsReadNotifierSubject.next();
    } finally {
      this.markAllAsReadLoadingSubject.next(false);
    }
  }

  public async requestAppointment(
    request: HelpAssistAppointmentRequest,
  ): Promise<HelpAssistTicketReference> {
    const ticketId = await firstExistentValueFrom(
      this.prefixedRequest.post({
        prefix: "site",
        path: "/HelpAssistTickets/RequestAppointment",
        data: request,
      }),
    );

    return { id: ticketId, site: request.site };
  }

  public async updateAppointmentApproval(
    request: HelpAssistTicketApproveOrDeclineRequest,
  ): Promise<boolean | null> {
    return await firstValueFrom(
      this.prefixedRequest
        .post({
          prefix: "site",
          path: "/HelpAssistTickets/ManageAppointmentApproval",
          data: request,
        })
        .pipe(
          // TODO: Remove this workaround when the startWith(null) is removed from the RequestService.post method,
          // Ignore the initial null emission from the POST request, which is used to indicate the loading state,
          skip(1),
        ),
    );
  }

  public async hasScheduledTicketFor(
    data: HelpAssistHasScheduledTicketForRequest,
  ): Promise<HelpAssistTicketReference | null> {
    const foundTicketId = await firstValueFrom(
      this.prefixedRequest
        .post({
          prefix: "site",
          path: "/HelpAssistTickets/getScheduledTicketId",
          data,
        })
        // TODO: Remove this workaround when the startWith(null) is removed from the RequestService.post method,
        // Ignore the initial null emission from the POST request, which is used to indicate the loading state,
        .pipe(skip(1)),
    );

    return foundTicketId ? { id: foundTicketId, site: data.site } : null;
  }

  public async updateDetails(
    request: HelpAssistTicketDetailsUpdateRequest,
  ): Promise<PartnerHelpAssistTicket> {
    const updatedTicket = await firstValueFrom(
      this.prefixedRequest.put({
        prefix: "site",
        path: "/HelpAssistTickets/{id}",
        pathParams: { id: request.ticket.id },
        data: request,
        mapResponse: (response, site) =>
          PartnerHelpAssistTicket.deserialize(response, { site }),
      }),
    );

    this.receivedTicketSubject.next(updatedTicket);
    return updatedTicket;
  }

  public async addAttachment(
    ticketId: HelpAssistTicketReference["id"],
    file: File,
  ): Promise<Attachment> {
    return this.attachments.create(ticketId, file);
  }

  public async deleteAttachmentFile(attachment: Attachment): Promise<void> {
    await this.attachments.delete(attachment);
  }

  public async addComment(
    update: HelpAssistEventCreation,
  ): Promise<HelpAssistEvent["id"]> {
    const eventId = await this.helpTicketEvents.post(update);
    await this.getSelected(update.ticket.id, false);
    return eventId;
  }

  public async create(
    data: HelpAssistTicketCreate,
  ): Promise<PartnerHelpAssistTicket> {
    const createdTicket = await firstExistentValueFrom(
      this.prefixedRequest.post({
        prefix: "site",
        path: "/HelpAssistTickets/CreateHelpAssistTicket",
        data,
        mapResponse: (response, site) =>
          PartnerHelpAssistTicket.deserialize(response, { site }),
      }),
    );

    this.ticketUpdateSubject.next(createdTicket);
    return createdTicket;
  }

  private async get(
    id: HelpAssistTicket["id"],
  ): Promise<PartnerHelpAssistTicket> {
    const ticket = await firstExistentValueFrom(
      this.prefixedRequest.get({
        prefix: "site",
        path: "/HelpAssistTickets/{id}",
        pathParams: { id },
        mapResponse: (response, site) =>
          PartnerHelpAssistTicket.deserialize(response, { site }),
      }),
    );

    this.ticketUpdateSubject.next(ticket);
    return ticket;
  }
}

export const isModifyTicketDisabled = ({
  ticket,
  site,
  userPermissions,
}: {
  ticket: HelpAssistTicket | null;
  site: Site;
  userPermissions: ReadonlySet<Permission>;
}): boolean => {
  if (ticket === null) {
    return true;
  }

  if (ticket.status === HelpAssistTicketStatus.Closed) {
    if (
      !ticket.category &&
      site.helpAssistTicketCategories.filter(
        (category) => category.ticketType === ticket.type,
      ).length === 0
    ) {
      return true;
    }
    return !userPermissions.has(Permission.EditHelpAssistTicketAdvanced);
  } else {
    return (
      ticket.status === HelpAssistTicketStatus.Expired ||
      ticket.type === HelpAssistTicketType.General
    );
  }
};
